/**
 * ARKO-Routing V2 (kein rohes CoT, keine PII/Aktenzitate).
 *
 * AI-Act-Systeminventar (Transparenz, Labor): Feldnamen/Labels in ArkoReasoningTraceCard
 * sind 1:1 übernehmbar — intent, path (Route), confidence, sources (norm_id), workflow_step.
 */

export interface ReasoningTraceSource {
  norm_id: string;
}

export interface ReasoningTraceStep {
  step: string;
  ok: boolean;
  detail?: string;
  ms?: number;
  chars?: number;
}

export interface ReasoningTrace {
  intent: string;
  path: string;
  not_triggered: string[];
  heuristic_reason?: string;
  mode?: string;
  confidence?: number;
  /** Nur gesetzt, wenn eine geeichte Version existiert. Sonst keine Wahrscheinlichkeit. */
  confidence_version?: string;
  workflow_step?: string;
  alternatives?: string[];
  /** LEXCORE-Norm-IDs (ohne Volltext) — AI-Act-Quellenfeld. */
  sources?: ReasoningTraceSource[];
  /** Web-Artikel-Fetch-Metadaten (kein Volltext). */
  web_fetch?: {
    url: string;
    host: string;
    allowed: boolean;
    chars: number;
  };
  regel_id?: string;
  motor_audit_id?: string;
  execution_id?: string;
  request_id?: string;
  pipeline: ReasoningTraceStep[];
  warnings: string[];
  error: string | null;
}

const ROUTING_LABELS: Record<string, string> = {
  frist_vorschlag: 'Frist-Vorschlag',
  schreiben_erstellen: 'Schreiben erstellen',
  rechtsfrage: 'Rechtsfrage',
  judicature_recherche: 'OGH-Judikatur',
  web_recherche: 'Internet-Recherche',
  vertrag_erstellen: 'Vertrag erstellen',
  allrounder: 'Allrounder',
  frist_heuristic_chat_doc: 'Frist-Hinweis (Chat + Dokument)',
  frist_heuristic_document: 'Frist-Hinweis (Dokument)',
  lexcore_rechtsfrage: 'Rechtsfrage',
  allrounder_fallback: 'Allgemeine Orientierung',
  background_document_analysis: 'Hintergrundanalyse (Dokument)',
  document_question_override_summary: 'Dokument-Frage → Zusammenfassung/Meta',
  document_question_override_analyze: 'Dokument-Frage → Analyse',
  document_question_override_lexcore: 'Dokument-Frage → Normen',
  chip_override_summary_meta: 'Chip-Override (Zusammenfassung/Meta)',
  override_summary_meta: 'Override Zusammenfassung/Meta',
  summary_or_meta: 'Zusammenfassung/Meta',
  section_or_law_match: '§-/Gesetzes-Match',
  keyword_match: 'Schlüsselwort-Match',
  topic_with_documentContext: 'Thema + Dokumentkontext',
  sachverhalt_analyse: 'Sachverhaltsanalyse (Subsumtion)',
  analyse_hub_lexcore: 'Analysieren → Normen',
  nachbar_immission_topic: 'Nachbar/Immission → Normen',
  paragraph_wish_topic: '§§-Wunsch + Thema',
  lexcore_analyse_hub_lexcore: 'Analysieren → Normen',
  lexcore_sachverhalt_analyse: 'Sachverhaltsanalyse (Subsumtion)',
  contract_create: 'Vertrag erstellen',
  schreiben_create: 'Schreiben erstellen',
  document_welcome: 'Dokument-Begrüßung',
  frist_vorschlag_intent: 'Expliziter Frist-Intent',
  lexcore: 'Normen',
  ollama: 'Allgemeine Antwort',
  direct: 'Direktantwort',
  frist_motor: 'Fristberechnung',
  lexcore_answer: 'Normen-Antwort',
  judicature_answer: 'Judikatur-Antwort',
  judicature_premium: 'OGH-Recherche (Premium)',
  judicature_empty: 'OGH-Recherche ohne Treffer',
  judicature_unavailable: 'OGH-Recherche nicht verfügbar',
  web_search_gate: 'Internet-Recherche',
  web_search_ok: 'Internet-Recherche',
  web_search_empty: 'Internet-Recherche ohne Treffer',
  web_search_stub: 'Internet-Recherche (ohne Treffer-Nachweis)',
  web_search_rejected: 'Internet-Recherche abgelehnt',
  web_search_unavailable: 'Internet-Recherche nicht erreichbar',
  premium_entitlement_gate: 'Premium-Zugang',
  schreiben_draft: 'Schreiben-Entwurf',
  vertrag_generator: 'Vertragsgenerator',
  intent_detect: 'Einordnung',
  normalize: 'Einordnung',
  document_context: 'Dokumentkontext',
  frist_probe: 'Fristprüfung',
  llm: 'Antwort formulieren',
};

const MODE_LABELS: Record<string, string> = {
  lexcore: 'Normen',
  ollama: 'Allgemein',
  direct: 'Direkt',
  judicature: 'Judikatur',
  web_search: 'Internet-Recherche',
};

/** Doppelte Pfad-Keys (z. B. allrounder_allrounder) → kanonischer Intent. */
export function normalizeRoutingKey(key: string): string {
  const k = key.trim();
  if (!k) return k;
  const slash = k.indexOf('/');
  if (slash > 0) {
    const head = k.slice(0, slash);
    const tail = k.slice(slash + 1);
    if (head && head === tail) return head;
  }
  const parts = k.split('_').filter(Boolean);
  if (parts.length >= 2 && parts.every((p) => p === parts[0])) {
    return parts[0];
  }
  return k;
}

export function routingLabel(key: string): string {
  const k = normalizeRoutingKey(key.trim());
  if (!k) return '—';
  return ROUTING_LABELS[k] ?? k.replace(/_/g, ' ');
}

export function modeLabel(mode: string | undefined): string {
  const k = String(mode ?? '').trim().toLowerCase();
  if (!k) return '—';
  return MODE_LABELS[k] ?? routingLabel(k);
}

export function confidenceLabel(confidence: number | undefined): string {
  if (confidence == null || !Number.isFinite(confidence)) return '—';
  const pct = Math.round(Math.max(0, Math.min(1, confidence)) * 100);
  return `${pct} %`;
}

function hasJudicatureEvidence(trace: ReasoningTrace, fallback: boolean): boolean {
  if (fallback) return false;
  const mode = String(trace.mode || '').toLowerCase();
  const step = String(trace.workflow_step || '').toLowerCase();
  const blob = `${trace.path || ''} ${trace.intent || ''} ${trace.heuristic_reason || ''} ${step}`.toLowerCase();
  return (
    mode === 'judicature' ||
    step.includes('judicature') ||
    blob.includes('judicature') ||
    blob.includes('premium_entitlement')
  );
}

function hasWebSearchEvidence(trace: ReasoningTrace, fallback: boolean): boolean {
  if (fallback) return false;
  const mode = String(trace.mode || '').toLowerCase();
  const step = String(trace.workflow_step || '').toLowerCase();
  const blob = `${trace.path || ''} ${trace.intent || ''} ${trace.heuristic_reason || ''} ${step}`.toLowerCase();
  return (
    mode === 'web_search' ||
    step.includes('web_search') ||
    blob.includes('web_search') ||
    blob.includes('web_recherche')
  );
}

/**
 * Trail defaultete oft intent/path=allrounder (70 %), obwohl LEXCORE oder JUD gelaufen ist
 * (verified_stamp / [LEX] / [JUD] / sources). Anzeige = tatsächlich gelaufener Zweig.
 */
export function reconcileExecutedRoute(trace: ReasoningTrace): ReasoningTrace {
  const fallback = (trace.warnings ?? []).some((w) => /allrounder_fallback/.test(w));
  const mode = String(trace.mode || '').toLowerCase();
  const step = String(trace.workflow_step || '').toLowerCase();
  const intentIsDefault = !trace.intent || trace.intent === 'allrounder';
  const pathIsDefault = !trace.path || trace.path === 'allrounder' || trace.path === 'allrounder_allrounder';

  if (hasJudicatureEvidence(trace, fallback) && (intentIsDefault || pathIsDefault)) {
    const reason = String(trace.heuristic_reason || '').trim();
    const judPath =
      !pathIsDefault && String(trace.path).toLowerCase().includes('judicature')
        ? trace.path
        : reason.startsWith('judicature')
          ? reason
          : step.includes('premium_entitlement')
            ? 'premium_entitlement_gate'
            : 'judicature_premium';
    return {
      ...trace,
      intent: intentIsDefault ? 'judicature_recherche' : trace.intent,
      path: pathIsDefault ? judPath : trace.path,
      mode: trace.mode || 'judicature',
      workflow_step: trace.workflow_step || 'judicature_answer',
    };
  }

  if (hasWebSearchEvidence(trace, fallback) && (intentIsDefault || pathIsDefault)) {
    const reason = String(trace.heuristic_reason || '').trim();
    const webPath =
      !pathIsDefault && String(trace.path).toLowerCase().includes('web_search')
        ? trace.path
        : reason.startsWith('web_search')
          ? reason
          : 'web_search_ok';
    return {
      ...trace,
      intent: intentIsDefault ? 'web_recherche' : trace.intent,
      path: pathIsDefault ? webPath : trace.path,
      mode: trace.mode || 'web_search',
      workflow_step: trace.workflow_step || 'web_search_gate',
    };
  }

  const hasLexEvidence =
    !fallback &&
    ((trace.sources?.length ?? 0) > 0 ||
      mode === 'lexcore' ||
      mode === 'direct' ||
      step.includes('lexcore') ||
      /\banalyse_hub_lexcore\b/.test(trace.heuristic_reason || ''));
  if (!hasLexEvidence) return trace;
  if (!intentIsDefault && !pathIsDefault) return trace;
  const reason = String(trace.heuristic_reason || '').trim();
  const path = reason
    ? reason.indexOf('lexcore') >= 0 || reason === 'sachverhalt_analyse' || reason === 'section_or_law_match'
      ? reason.startsWith('lexcore_')
        ? reason
        : `lexcore_${reason}`
      : 'lexcore_rechtsfrage'
    : 'lexcore_rechtsfrage';
  return {
    ...trace,
    intent: intentIsDefault ? 'rechtsfrage' : trace.intent,
    path: pathIsDefault ? path : trace.path,
    mode: trace.mode || 'lexcore',
    workflow_step: trace.workflow_step || 'lexcore_answer',
  };
}

export function executedRoutePrimaryLabel(trace: ReasoningTrace): string {
  const t = reconcileExecutedRoute(trace);
  const blob = `${t.path} ${t.intent} ${t.heuristic_reason || ''}`.toLowerCase();
  if (blob.includes('summary')) return 'Zusammenfassen';
  const hasLexSources = (t.sources?.length ?? 0) > 0;
  const category = classifyAnswerPathCategory(
    t.path,
    t.intent,
    t.warnings,
    hasLexSources,
    hasJudicatureEvidence(t, false),
  );
  if (category === 'lexcore') return 'Normen';
  if (category === 'judicature') return 'OGH-Judikatur';
  if (category === 'web_search') return 'Internet-Recherche';
  if (category === 'frist') return routingLabel(t.path) || 'Frist';
  if (category === 'schreiben') return routingLabel(t.path) || 'Schreiben';
  if (category === 'dokument') return 'Dokument';
  if (t.intent === 'allrounder' || t.path === 'allrounder') return 'Allrounder';
  return 'Orientierung';
}

export function tracePathSummary(trace: ReasoningTrace): string {
  const t = reconcileExecutedRoute(trace);
  const primary = executedRoutePrimaryLabel(t);
  const pathLbl = routingLabel(t.path);
  const parts = [primary];
  if (pathLbl && pathLbl !== '—' && pathLbl !== primary && pathLbl !== 'Allrounder') {
    if (!(primary === 'Normen' && (pathLbl === 'Rechtsfrage' || pathLbl === 'Normen'))) {
      parts.push(pathLbl);
    }
  }
  if (t.confidence != null && t.confidence_version) parts.push(confidenceLabel(t.confidence));
  return parts.join(' · ');
}

export function parseReasoningTrace(raw: unknown): ReasoningTrace | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const intent = String(o.intent ?? '').trim();
  const path = String(o.path ?? '').trim();
  if (!intent && !path) return null;
  const notRaw = o.not_triggered;
  const not_triggered = Array.isArray(notRaw)
    ? notRaw.map((x) => String(x).trim()).filter(Boolean)
    : [];
  const trace: ReasoningTrace = {
    intent: normalizeRoutingKey(intent || 'allrounder'),
    path: normalizeRoutingKey(path || intent || 'allrounder'),
    not_triggered,
    pipeline: [],
    warnings: [],
    error: null,
  };
  const heuristicReason = String(o.heuristic_reason ?? '').trim();
  if (heuristicReason) trace.heuristic_reason = heuristicReason;
  const mode = String(o.mode ?? '').trim();
  if (mode) trace.mode = mode;
  const confidenceVersion = String(o.confidence_version ?? '').trim();
  const confidence = Number(o.confidence);
  if (
    confidenceVersion
    && confidenceVersion !== 'heuristic'
    && Number.isFinite(confidence)
    && confidence >= 0
    && confidence <= 1
  ) {
    trace.confidence = confidence;
    trace.confidence_version = confidenceVersion;
  }
  const workflowStep = String(o.workflow_step ?? '').trim();
  if (workflowStep) trace.workflow_step = workflowStep;
  const altRaw = o.alternatives;
  const alternatives = Array.isArray(altRaw)
    ? altRaw.map((x) => String(x).trim()).filter(Boolean)
    : [];
  if (alternatives.length > 0) trace.alternatives = alternatives.map(normalizeRoutingKey);
  const sourcesRaw = o.sources;
  if (Array.isArray(sourcesRaw)) {
    const sources = sourcesRaw.flatMap((entry): ReasoningTraceSource[] => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
      const normId = String((entry as Record<string, unknown>).norm_id ?? '').trim();
      return normId ? [{ norm_id: normId }] : [];
    });
    if (sources.length > 0) trace.sources = sources;
  }
  const regel = String(o.regel_id ?? '').trim();
  if (regel) trace.regel_id = regel;
  const motor = String(o.motor_audit_id ?? '').trim();
  if (motor) trace.motor_audit_id = motor;
  const exec = String(o.execution_id ?? '').trim();
  if (exec) trace.execution_id = exec;
  const rid = String(o.request_id ?? '').trim();
  if (rid) trace.request_id = rid;
  const webFetchRaw = o.web_fetch;
  if (webFetchRaw && typeof webFetchRaw === 'object' && !Array.isArray(webFetchRaw)) {
    const wf = webFetchRaw as Record<string, unknown>;
    const wfUrl = String(wf.url ?? '').trim();
    const wfHost = String(wf.host ?? '').trim();
    if (wfUrl && wfHost) {
      trace.web_fetch = {
        url: wfUrl,
        host: wfHost,
        allowed: wf.allowed === true,
        chars: Number.isFinite(Number(wf.chars)) ? Math.max(0, Math.round(Number(wf.chars))) : 0,
      };
    }
  }
  trace.pipeline = (Array.isArray(o.pipeline) ? o.pipeline : []).flatMap((entry): ReasoningTraceStep[] => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
    const rawStep = entry as Record<string, unknown>;
    const step = String(rawStep.step ?? '').trim();
    if (!step) return [];
    const parsed: ReasoningTraceStep = { step, ok: rawStep.ok === true };
    const detail = String(rawStep.detail ?? '').trim();
    if (detail) parsed.detail = detail;
    const ms = Number(rawStep.ms);
    if (Number.isFinite(ms) && ms >= 0) parsed.ms = Math.round(ms);
    const chars = Number(rawStep.chars);
    if (Number.isFinite(chars) && chars >= 0) parsed.chars = Math.round(chars);
    return [parsed];
  });
  trace.warnings = Array.isArray(o.warnings)
    ? o.warnings.map((warning) => String(warning).trim()).filter(Boolean)
    : [];
  const error = String(o.error ?? '').trim();
  trace.error = error || null;
  return reconcileExecutedRoute(trace);
}

function warningMeansNothingFound(warning: string): boolean {
  return /lexcore_miss|no_match|threshold|trefferschwelle/i.test(warning);
}

function lawyerSupportHint(warning: string): string {
  const k = warning.trim().toLowerCase();
  if (!k) return '';
  if (/lexcore_miss|no_match|trefferschwelle|threshold/.test(k)) return 'nichts gefunden';
  if (/allrounder_fallback/.test(k)) return 'allgemeine Orientierung';
  if (/lexcore/.test(k)) return 'Nachschlagen';
  return warning.replace(/_/g, ' ').replace(/lexcore/gi, 'Nachschlagen');
}

function everydayStepName(step: string): 'Normen' | 'Judikatur' | 'Web' | 'Akte' | 'Formulierung' | null {
  const key = step.toLowerCase();
  if (/judic|judikatur|\bogh\b/.test(key)) return 'Judikatur';
  if (/web_search|web_recherche|\bweb\b/.test(key)) return 'Web';
  if (/document|akte|extract/.test(key)) return 'Akte';
  if (/llm|formul|draft|schreiben|\banswer\b/.test(key)) return 'Formulierung';
  if (/lex|norm|rechtsfrage/.test(key)) return 'Normen';
  return null;
}

function stepFoundNothing(step: ReasoningTraceStep): boolean {
  const detail = String(step.detail || '');
  return step.ok === false || /miss|empty|no_match|threshold/i.test(detail);
}

/** Laufdaten für die Spur — Badge und Kanäle, die in diesem Lauf wirklich da waren. */
export interface ReasoningNarrativeFacts {
  /** true, wenn das Badge dieser Antwort mitgegeben wurde (auch „nicht belegt“). */
  badgeKnown: boolean;
  /** Norm-IDs nur bei Badge „belegt“. Leer = nicht belegt. */
  verifiedNormIds: string[];
  /** RIS nur, wenn der Beleg eine RIS-Adresse hat. Sonst Normbestand. */
  normSource?: string;
  answerText?: string;
  /** undefined = aus der Pipeline lesen. true = Nachweis sagt, der Kanal lief. */
  judicatureRan?: boolean;
  judicatureHit?: boolean;
  webRan?: boolean;
  webHit?: boolean;
  webSource?: string;
}

function lawyerNormLabel(normId: string): string {
  const raw = String(normId || '').trim();
  const match = raw.match(/^([A-Za-zÄÖÜ][A-Za-zÄÖÜ0-9.-]{1,14})-(\d{1,4}[a-zA-Z]?)$/i);
  if (!match) return raw;
  const section = match[2].replace(/[A-Za-z]+$/, (letters) => letters.toLowerCase());
  return `§ ${section} ${match[1].toUpperCase()}`;
}

function detailSkipped(detail: string | undefined): boolean {
  const value = String(detail || '').trim();
  if (!value) return false;
  return /^(skipped|missing)$/i.test(value) || /^skipped_/i.test(value);
}

function stepsNamed(
  trace: ReasoningTrace,
  name: 'Normen' | 'Judikatur' | 'Web' | 'Akte' | 'Formulierung',
): ReasoningTraceStep[] {
  return (trace.pipeline ?? []).filter((step) => everydayStepName(step.step) === name);
}

function channelRan(
  trace: ReasoningTrace,
  name: 'Normen' | 'Judikatur' | 'Web' | 'Akte' | 'Formulierung',
): { ran: boolean; nothing: boolean } {
  const steps = stepsNamed(trace, name);
  if (!steps.length) return { ran: false, nothing: false };
  const ranSteps = steps.filter((step) => !detailSkipped(step.detail));
  if (!ranSteps.length) return { ran: false, nothing: false };
  return { ran: true, nothing: ranSteps.every(stepFoundNothing) };
}

function akteWasInRun(trace: ReasoningTrace): boolean {
  return stepsNamed(trace, 'Akte').some((step) => {
    if (detailSkipped(step.detail)) return false;
    if ((step.chars ?? 0) > 0) return true;
    return /present/i.test(String(step.detail || ''));
  });
}

function everydayEinordnung(trace: ReasoningTrace): string {
  const category = classifyAnswerPathCategory(
    trace.path,
    trace.intent,
    trace.warnings,
    (trace.sources?.length ?? 0) > 0,
    hasJudicatureEvidence(trace, false),
  );
  if (category === 'frist') return 'Fristen';
  if (category === 'schreiben') return 'Schreiben';
  if (category === 'judicature' || category === 'web_search') return 'Recherche';
  if (category === 'lexcore') return 'Normen nachschlagen';
  if (category === 'dokument') return 'Dokument';
  return 'eine allgemeine Frage';
}

/** Judikatur, Web, weitere Normen — Formulierung ist kein Kanal. */
function leftOutChannel(raw: string): 'Judikatur' | 'Web' | 'weitere Normen' | null {
  const key = raw.toLowerCase();
  if (/judic|judikatur|\bogh\b/.test(key)) return 'Judikatur';
  if (/web_search|web_recherche|\bweb\b/.test(key)) return 'Web';
  if (/rechtsfrage|lexcore|\blex\b|norm/.test(key)) return 'weitere Normen';
  return null;
}

/**
 * Alltagssätze für die zugeklappte Spur „So bin ich vorgegangen“.
 * Voller Weg eines Laufs: Einordnung, Nachschlagen, Gelesenes, eingeflossen, nicht eingeflossen.
 * Keine Kennzahlen. Formulierung ist kein fehlender Kanal.
 */
export function buildReasoningNarrative(
  trace: ReasoningTrace,
  facts?: ReasoningNarrativeFacts,
): string[] {
  const t = reconcileExecutedRoute(trace);
  const lines = [`Eingeordnet: ${everydayEinordnung(t)}.`];
  const category = classifyAnswerPathCategory(
    t.path,
    t.intent,
    t.warnings,
    (t.sources?.length ?? 0) > 0,
    hasJudicatureEvidence(t, false),
  );
  const badgeHit = Boolean(facts?.badgeKnown && (facts.verifiedNormIds?.length ?? 0) > 0);
  const norms = channelRan(t, 'Normen');
  const normsRan =
    badgeHit ||
    norms.ran ||
    (!facts?.badgeKnown &&
      ((t.sources?.length ?? 0) > 0 ||
        (t.warnings ?? []).some(warningMeansNothingFound) ||
        category === 'lexcore'));
  const normIds = badgeHit
    ? facts!.verifiedNormIds
    : facts?.badgeKnown
      ? []
      : (t.sources ?? []).map((source) => source.norm_id).filter(Boolean);
  const normLabels = normIds.map(lawyerNormLabel).filter(Boolean);
  const normHit = normLabels.length > 0;

  if (akteWasInRun(t)) {
    lines.push('Gelesen: das Dokument.');
  }
  if (normsRan) {
    lines.push('Schritt Normen: nachgeschlagen.');
    if (normHit) {
      const source = facts?.normSource === 'RIS' ? 'RIS' : 'Normbestand';
      lines.push(`Nachgeschlagen ${normLabels.join(', ')}: Treffer. Quelle: ${source}.`);
    } else {
      lines.push('Nachgeschlagen: kein Treffer. Quelle: Normbestand.');
    }
  }

  const judPipeline = channelRan(t, 'Judikatur');
  const judRan =
    facts?.judicatureRan === true ||
    (facts?.judicatureRan == null && (judPipeline.ran || category === 'judicature'));
  const judHit = judRan
    ? facts?.judicatureRan === true
      ? facts.judicatureHit === true
      : judPipeline.ran
        ? !judPipeline.nothing
        : false
    : false;
  if (judRan) {
    lines.push('Schritt Judikatur: nachgesehen.');
    lines.push(
      judHit
        ? 'Nachgeschlagen Judikatur: Treffer. Quelle: Judikatur.'
        : 'Nachgeschlagen Judikatur: kein Treffer. Quelle: Judikatur.',
    );
  }

  const webPipeline = channelRan(t, 'Web');
  const webRan =
    facts?.webRan === true || (facts?.webRan == null && (webPipeline.ran || category === 'web_search'));
  const webHit = webRan
    ? facts?.webRan === true
      ? facts.webHit === true
      : webPipeline.ran && !webPipeline.nothing
    : false;
  if (webRan) {
    lines.push('Schritt Web: nachgesehen.');
    lines.push(
      webHit
        ? 'Nachgeschlagen Web: Treffer. Quelle: Web.'
        : 'Nachgeschlagen Web: kein Treffer. Quelle: Web.',
    );
  }
  if (category === 'frist') {
    lines.push('Schritt Fristen: geprüft.');
  }

  const flowed: string[] = [];
  if (normHit) flowed.push(normLabels.length === 1 ? 'diese Norm' : 'diese Normen');
  if (judHit || webHit) flowed.push('diese Quellen');
  lines.push(
    flowed.length
      ? `In die Antwort eingeflossen: ${flowed.join(' und ')}.`
      : 'In die Antwort eingeflossen: keine belegte Norm.',
  );

  const leftOut = new Set<'Judikatur' | 'Web' | 'weitere Normen'>();
  for (const raw of t.not_triggered ?? []) {
    const channel = leftOutChannel(raw);
    if (channel) leftOut.add(channel);
  }
  for (const step of t.pipeline ?? []) {
    if (!detailSkipped(step.detail)) continue;
    const channel = leftOutChannel(step.step);
    if (channel) leftOut.add(channel);
  }
  if (normsRan && !normHit) leftOut.add('weitere Normen');
  if (judRan && !judHit) leftOut.add('Judikatur');
  if (webRan && !webHit) leftOut.add('Web');
  if (judHit) leftOut.delete('Judikatur');
  if (webHit) leftOut.delete('Web');
  if (normHit) leftOut.delete('weitere Normen');
  if (leftOut.size) {
    const order = ['Judikatur', 'Web', 'weitere Normen'] as const;
    lines.push(`Nicht eingeflossen: ${order.filter((name) => leftOut.has(name)).join(', ')}.`);
  }
  return lines.map((line) => line.replace(/lexcore/gi, 'Normen'));
}

/** Support-Kopierblock: Vorgang und Alltagssätze. Keine internen Kürzel wie lexcore_miss. */
export function buildReasoningTraceSupportCopy(
  trace: ReasoningTrace,
  facts?: ReasoningNarrativeFacts,
): string {
  const lines = ['ARKO Support-Referenz'];
  if (trace.request_id) lines.push(`Vorgang: ${trace.request_id}`);
  if (trace.execution_id) lines.push(`Lauf: ${trace.execution_id}`);
  const narrative = buildReasoningNarrative(trace, facts);
  for (const sentence of narrative) lines.push(sentence);
  const badgeHit = Boolean(facts?.badgeKnown && (facts.verifiedNormIds?.length ?? 0) > 0);
  const hints = [...new Set((trace.warnings ?? []).map(lawyerSupportHint).filter(Boolean))].filter(
    (hint) => !(badgeHit && hint === 'nichts gefunden'),
  );
  if (hints.length) lines.push(`Hinweis: ${hints.join(', ')}`);
  if (trace.error) lines.push(`Fehler: ${lawyerSupportHint(trace.error)}`);
  return lines.join('\n').replace(/lexcore/gi, 'Nachschlagen');
}

export function hasReasoningTraceData(trace: ReasoningTrace | null | undefined): boolean {
  return Boolean(
    trace && (trace.intent || trace.path || trace.workflow_step || trace.heuristic_reason),
  );
}

/** Antwort-Pfad-Kategorie — dezente Bot-Bubble-Akzente (AI-Act: sichtbare Route, nicht nur Farbe). */
export type AnswerPathCategory =
  | 'orientierung'
  | 'lexcore'
  | 'judicature'
  | 'web_search'
  | 'dokument'
  | 'schreiben'
  | 'frist';

export interface AnswerPathStyle {
  category: AnswerPathCategory;
  /** CSS-Farbe für linken Rand (2–3 px), ohne Glow. */
  borderColor: string;
  /** Barrierefreies Text-Label — immer neben/zu der Farbe anzeigen. */
  label: string;
  /** Textfarbe für das Label (Theme-kompatibel, CURSOR_PORTAL dark). */
  labelColor: string;
}

/**
 * AI-Act-Transparenz (Labor): `intent` + `path` aus `reasoning_trace` werden als
 * lesbares Pfad-Label + dezenter Rand an der Bot-Antwort gespiegelt — gleiche
 * Semantik wie ArkoReasoningTraceCard (Route), ohne Roh-CoT.
 */
const ANSWER_PATH_STYLES: Record<AnswerPathCategory, AnswerPathStyle> = {
  orientierung: {
    category: 'orientierung',
    borderColor: 'rgba(148, 163, 184, 0.42)',
    label: 'Orientierung',
    labelColor: 'rgba(148, 163, 184, 0.88)',
  },
  lexcore: {
    category: 'lexcore',
    borderColor: 'rgba(96, 165, 250, 0.48)',
    label: 'Normen',
    labelColor: 'rgba(147, 197, 253, 0.92)',
  },
  judicature: {
    category: 'judicature',
    borderColor: 'rgba(167, 139, 250, 0.52)',
    label: 'Judikatur',
    labelColor: 'rgba(196, 181, 253, 0.94)',
  },
  web_search: {
    category: 'web_search',
    borderColor: 'rgba(56, 189, 248, 0.52)',
    label: 'Recherche',
    labelColor: 'rgba(125, 211, 252, 0.94)',
  },
  dokument: {
    category: 'dokument',
    borderColor: 'rgba(167, 139, 250, 0.45)',
    label: 'Dokument',
    labelColor: 'rgba(196, 181, 253, 0.9)',
  },
  schreiben: {
    category: 'schreiben',
    borderColor: 'rgba(251, 191, 36, 0.42)',
    label: 'Schreiben',
    labelColor: 'rgba(253, 224, 71, 0.88)',
  },
  frist: {
    category: 'frist',
    borderColor: 'rgba(45, 212, 191, 0.42)',
    label: 'Frist',
    labelColor: 'rgba(94, 234, 212, 0.88)',
  },
};

const FRIST_PATH_RE =
  /\b(frist_vorschlag|frist_motor|frist_probe|frist_heuristic)\b/i;
const SCHREIBEN_PATH_RE =
  /\b(schreiben_erstellen|schreiben_create|schreiben_draft|vertrag_erstellen|vertrag_generator|contract_create|schreiben_create)\b/i;
const LEXCORE_PATH_RE =
  /\b(rechtsfrage|lexcore|section_or_law_match|paragraph_wish_topic)\b/i;
/** `_` ist Wortzeichen — `\bjudicature\b` matcht nicht `judicature_premium`. */
const JUDICATURE_PATH_RE =
  /(^|[|_])(judicature|judikatur|ogh|premium_entitlement_gate)(?=$|[|_])/i;
const WEB_SEARCH_PATH_RE =
  /(^|[|_])(web_search|web_recherche|web_gate)(?=$|[|_])/i;
const DOCUMENT_PATH_RE =
  /\b(document_context|document_welcome|background_document_analysis|document_question|topic_with_document|summary_or_meta|override_summary|chip_override_summary)\b/i;

function routingKeysForStyle(path: string, intent: string): string {
  const p = normalizeRoutingKey(path).toLowerCase();
  const i = normalizeRoutingKey(intent).toLowerCase();
  return [p, i, `${i}_${p}`, `${p}_${i}`].filter(Boolean).join('|');
}

function warningsHintLexcore(warnings: string[]): boolean {
  return warnings.some((w) => /\b(lexcore|rechtsfrage|norm_id|sources?)\b/i.test(w));
}

function warningsHintJudicature(warnings: string[]): boolean {
  return warnings.some((w) => /(judicature|judikatur|\[JUD:)/i.test(w));
}

function warningsHintWebSearch(warnings: string[]): boolean {
  return warnings.some((w) => /(web_search|web_recherche|ARKO_WEB_GATE|\[WEB:)/i.test(w));
}

/** Klassifikation intent/path → Antwort-Pfad-Stil (Priorität: Frist > Schreiben > JUD > LEX > Dokument > Orientierung). */
export function classifyAnswerPathCategory(
  path: string,
  intent: string,
  warnings: string[] = [],
  hasLexcoreSources = false,
  hasJudEvidence = false,
): AnswerPathCategory {
  const keys = routingKeysForStyle(path, intent);
  if (FRIST_PATH_RE.test(keys)) return 'frist';
  if (SCHREIBEN_PATH_RE.test(keys)) return 'schreiben';
  if (JUDICATURE_PATH_RE.test(keys) || hasJudEvidence || warningsHintJudicature(warnings)) {
    return 'judicature';
  }
  if (WEB_SEARCH_PATH_RE.test(keys) || warningsHintWebSearch(warnings)) {
    return 'web_search';
  }
  if (LEXCORE_PATH_RE.test(keys) || hasLexcoreSources || warningsHintLexcore(warnings)) {
    return 'lexcore';
  }
  if (DOCUMENT_PATH_RE.test(keys)) return 'dokument';
  return 'orientierung';
}

export function getAnswerPathStyle(
  path: string,
  intent: string,
  warnings: string[] = [],
  hasLexcoreSources = false,
  hasJudEvidence = false,
): AnswerPathStyle {
  const category = classifyAnswerPathCategory(path, intent, warnings, hasLexcoreSources, hasJudEvidence);
  return ANSWER_PATH_STYLES[category];
}

export function getAnswerPathStyleFromTrace(trace: ReasoningTrace | null | undefined): AnswerPathStyle | null {
  if (!trace || !hasReasoningTraceData(trace)) return null;
  const t = reconcileExecutedRoute(trace);
  const hasLexcoreSources = (t.sources?.length ?? 0) > 0;
  return getAnswerPathStyle(t.path, t.intent, t.warnings, hasLexcoreSources, hasJudicatureEvidence(t, false));
}
